/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import { useCallback, useEffect, useState } from "react";
import * as React from "react";
import { CompletedEnhanceSpeechTrack } from "components/EnhancedSpeech/Utilities";
import { ConditionalWrapper } from "components/shared/ConditionalWrapper";
import { OverlayTrigger } from "@spectrum-web-components/overlay";
import { useEventListener } from "hooks/useEventListener";
import {
  CloseButton,
  DownloadTrackButton,
} from "components/EnhancedSpeech/Buttons";
import { StrengthAdjuster } from "components/shared/StrengthAdjuster";
import EnhancedTrackPlayer from "components/EnhancedSpeech/EnhancedTrackPlayer";
import { webComponentShim } from "components/shared/WebComponentShim";
import { ModelVersionBadge } from "components/EnhancedSpeech/ProTierEnhanceSpeech/ModelVersionBadge";
import { useCurrentUser } from "actions/CurrentUserActions";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { EnhancedToggle } from "components/shared/EnhancedSwitch";
import "stylesheets/spectrum.scss";

const TrackDetailHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  grid-area: track-header;
  max-width: 100%;
  gap: 16px;
`;

const TrackDetailContainer = styled.div`
  grid-area: track-detail;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${devices.small} {
    gap: 16px;
    // give a little extra space for the play button highlight
    margin-left: -5px;
    margin-right: -5px;
    margin-top: -5px;
    margin-bottom: 24px;
    padding-top: 5px;
    padding-left: 5px;
    padding-right: 5px;
    padding-bottom: 24px;
  }
`;

const HeadingMedium = styled.h2`
  font: normal normal 900 22px/27px ${fontFamilies};
  color: var(--spectrum-global-color-gray-900);
  align-self: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-start;
  align-items: start;
  max-width: 70%;
`;

const MobileDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  width: 100%;
  min-height: 400px;

  h2 {
    max-width: calc(100% - 24px);
  }
`;

const MobileCloseButton = styled(CloseButton)`
  position: absolute;
  top: 32px;
  right: 24px;
`;

const FeedbackLink = styled.a.attrs({
  className: "spectrum-Link",
})`
  font-size: 16px;
  font-weight: 400;
`;

const EnhanceControlToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const EnhanceControlsContainer = styled.div.attrs({
  "data-testid": "enhance-container",
})`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 24px;
  background: var(--spectrum-indigo-100, #edeeff);
  gap: 16px;
  align-self: stretch;
  margin-bottom: 24px;

  ${devices.small} {
    margin-bottom: 0;
  }
`;

const TrayWrapper: React.FC<{ onClose: VoidFunction }> = ({
  children,
  onClose,
}) => {
  const [overlayRef, setOverlayRef] = useState<OverlayTrigger | null>(null);

  useEventListener("sp-closed", onClose, overlayRef);

  const attachRef = useCallback(
    (node: OverlayTrigger) => setOverlayRef(node),
    []
  );

  return (
    <overlay-trigger type="modal" open="click" ref={attachRef}>
      <FullWidthTray slot="click-content">
        <MobileDetailContainer data-testid="mobile-detail-container">
          <MobileCloseButton
            onClick={onClose}
            title={I18n.t("enhance_speech.close_mobile")}
          />
          {children}
        </MobileDetailContainer>
      </FullWidthTray>
      <sp-button slot="trigger" variant="primary" hidden={true} />
    </overlay-trigger>
  );
};

const FullWidthTray = styled(webComponentShim("sp-tray"))`
  --spectrum-dialog-max-width: 100%;
`;

// the values in the sliders are in percent
const ENHANCE_STRENGTH_PERCENT_DEFAULT = 90;
const BACKGROUND_PERCENT_DEFAULT = 10;
const ENHANCE_ENABLED_DEFAULT = true;

export interface TrackDetailProps {
  track: CompletedEnhanceSpeechTrack;
  onClose: VoidFunction;
  export: (
    enhanceStrength: number,
    backgroundStrength: number,
    enhancementEnabled: boolean
  ) => void;
  isExporting: boolean;
}

export const TrackDetail: React.FC<TrackDetailProps> = ({
  track,
  onClose,
  export: exportTrack,
  isExporting,
}) => {
  const isMobile = useIsMobile();
  const [enhanceStrengthPercent, setEnhanceStrengthPercent] = useState(
    ENHANCE_STRENGTH_PERCENT_DEFAULT
  );
  const [backgroundPercent, setBackgroundPercent] = useState(
    BACKGROUND_PERCENT_DEFAULT
  );
  const [enhanceEnabled, setEnhanceEnabled] = useState(ENHANCE_ENABLED_DEFAULT);

  const { currentUser } = useCurrentUser();

  const enhanceSpeechV2Enabled = Boolean(
    currentUser?.enhanceSpeechV2PremiumEnabled
  );

  useEffect(() => {
    setEnhanceStrengthPercent(ENHANCE_STRENGTH_PERCENT_DEFAULT);
  }, [track]);
  useEffect(() => {
    setBackgroundPercent(BACKGROUND_PERCENT_DEFAULT);
  }, [track]);
  useEffect(() => {
    setEnhanceEnabled(ENHANCE_ENABLED_DEFAULT);
  }, [track]);

  const showBackgroundSlider: boolean = track.modelVersion === ModelVersion.v2;

  const EnhancedToggleSlot = (
    <EnhanceControlToggle>
      <EnhancedToggle
        checked={enhanceEnabled}
        onClick={() => {
          setEnhanceEnabled(!enhanceEnabled);
        }}
      />
    </EnhanceControlToggle>
  );
  // children can't be nested under other elements because of css grid in parent.
  return (
    <ConditionalWrapper
      wrapperComponent={TrayWrapper}
      condition={isMobile}
      wrapperProps={{ onClose: onClose }}
    >
      <TrackDetailHeader data-testid="track-detail-header">
        <HeaderGroup>
          <HeadingMedium title={track.trackName}>
            {track.trackName}
          </HeadingMedium>
          <ModelVersionBadge>{track.modelVersion}</ModelVersionBadge>
        </HeaderGroup>
        {!isMobile && (
          <DownloadTrackButton
            disabled={isExporting}
            onClick={(): void =>
              exportTrack(
                enhanceStrengthPercent,
                backgroundPercent,
                enhanceEnabled
              )
            }
          />
        )}
      </TrackDetailHeader>
      <TrackDetailContainer>
        <EnhancedTrackPlayer
          track={track}
          enhanceStrengthPercent={enhanceStrengthPercent}
          backgroundPercent={backgroundPercent}
          enhancementEnabled={enhanceEnabled}
        />
        <EnhanceControlsContainer>
          <StrengthAdjuster
            enhanceStrengthPercent={enhanceStrengthPercent}
            onChange={setEnhanceStrengthPercent}
            backgroundPercent={backgroundPercent}
            onChangeBackground={setBackgroundPercent}
            enhanceEnabled={enhanceEnabled}
            showBackgroundSlider={showBackgroundSlider}
            toggleSlot={EnhancedToggleSlot}
          />
        </EnhanceControlsContainer>
        {!isMobile && !enhanceSpeechV2Enabled && (
          <FeedbackLink target="_blank" href="/enhance-speech-v2">
            {I18n.t("enhance_speech_v2_early_access_page.promo_link_text")}
          </FeedbackLink>
        )}

        {isMobile && (
          <DownloadTrackButton
            disabled={isExporting}
            onClick={(): void =>
              exportTrack(
                enhanceStrengthPercent,
                backgroundPercent,
                enhanceEnabled
              )
            }
          />
        )}
      </TrackDetailContainer>
    </ConditionalWrapper>
  );
};
